import React, { createContext, useState, ReactNode, useEffect, useCallback, useRef, useMemo } from "react";
import { AppState, AppStateStatus } from "react-native";
import { supabase } from "@/lib/supabase-client";
import useAuth from "@/hooks/useAuth";
import { twilioSmsService, SMSMessage } from "@/services/twilioSmsService";
import { getPhoneDisplayName, PhoneConfig } from "@/lib/phone";

export interface SMSPhoneConfig extends PhoneConfig {
  displayName: string;
}

type SMSMessageListener = (message: SMSMessage) => void;

export interface SMSContextType {
  unreadCounts: Record<string, number>;
  phones: SMSPhoneConfig[];
  totalUnread: number;
  refreshUnreadCount: (phoneNumber: string) => Promise<void>;
  refreshAllUnreadCounts: () => Promise<void>;
  markConversationRead: (phoneNumber: string, externalNumber: string) => void;
  // Register a listener for new messages on a Twilio number; returns an unsubscribe fn.
  // Backed by ONE shared postgres_changes subscription (see the realtime effect below),
  // so multiple consumers (drawer, conversation) never open competing channels.
  addMessageListener: (phoneNumber: string, listener: SMSMessageListener) => () => void;
}

export const SMSContext = createContext<SMSContextType | undefined>(undefined);

// Debounce period in ms
const DEBOUNCE_MS = 10000;

// Map a sms_messages row (postgres_changes payload.new) to the SMSMessage shape.
function mapRowToSMSMessage(row: any): SMSMessage {
  const inbound = row.direction === "inbound";
  return {
    sid: row.message_sid ?? `${row.twilio_number}-${row.external_number}-${row.created_at}`,
    body: row.body ?? "",
    from: inbound ? row.external_number : row.twilio_number,
    to: inbound ? row.twilio_number : row.external_number,
    direction: row.direction,
    dateSent: null,
    dateCreated: row.twilio_created_at ?? row.created_at,
    status: row.status ?? "",
  };
}

export function SMSProvider({ children }: { children: ReactNode }) {
  const { userRoles, user } = useAuth();

  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const lastFetchRef = useRef<Record<string, number>>({});
  // twilio number -> set of message listeners (drawer, conversation, …)
  const listenersRef = useRef<Map<string, Set<SMSMessageListener>>>(new Map());

  // Extract phones from user roles and compute displayName
  const phones = useMemo((): SMSPhoneConfig[] => {
    if (!userRoles) return [];

    const allPhones = userRoles.flatMap((r) => r.permissions?.phones || []);

    // Deduplicate by phone number, keeping first occurrence
    const uniqueMap = new Map<string, SMSPhoneConfig>();
    allPhones.forEach((phone: { number: string; alias?: string; show_number?: boolean }) => {
      if (!uniqueMap.has(phone.number)) {
        const config: PhoneConfig = {
          number: phone.number,
          alias: phone.alias,
          showNumber: phone.show_number !== false, // default to true if not specified
        };
        uniqueMap.set(phone.number, {
          ...config,
          displayName: getPhoneDisplayName(config),
        });
      }
    });

    return Array.from(uniqueMap.values());
  }, [userRoles]);

  // Mark a conversation as read (optimistic decrement + shared DB marker)
  const markConversationRead = useCallback(
    (twilioNumber: string, externalNumber: string) => {
      // Optimistic local decrement for snappy UI.
      setUnreadCounts((prev) => {
        const current = prev[twilioNumber] || 0;
        return current > 0 ? { ...prev, [twilioNumber]: Math.max(0, current - 1) } : prev;
      });
      // Persist the shared read marker (one row per conversation, shared by the team).
      void (async () => {
        const { error } = await supabase.from("sms_conversation_reads").upsert(
          {
            twilio_number: twilioNumber,
            external_number: externalNumber,
            last_read_at: new Date().toISOString(),
            last_read_by: user?.id ?? null,
          },
          { onConflict: "tenant_id,twilio_number,external_number" },
        );
        if (error) console.error("[SMSProvider] Failed to mark read:", error.message);
      })();
    },
    [user?.id],
  );

  // Fetch unread count for a specific phone number
  const refreshUnreadCount = useCallback(async (phoneNumber: string) => {
    const now = Date.now();
    if (lastFetchRef.current[phoneNumber] && now - lastFetchRef.current[phoneNumber] < DEBOUNCE_MS) {
      return;
    }
    lastFetchRef.current[phoneNumber] = now;
    try {
      const conversations = await twilioSmsService.getConversations(phoneNumber);
      const unreadCount = conversations.filter((c) => c.unread).length;
      setUnreadCounts((prev) => ({ ...prev, [phoneNumber]: unreadCount }));
    } catch (error) {
      console.error(`[SMSProvider] Failed to fetch unread count for ${phoneNumber}:`, error);
    }
  }, []);

  // Refresh all unread counts
  const refreshAllUnreadCounts = useCallback(async () => {
    await Promise.all(phones.map((phone) => refreshUnreadCount(phone.number)));
  }, [phones, refreshUnreadCount]);

  // Calculate total unread
  const totalUnread = useMemo(() => {
    return Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);
  }, [unreadCounts]);

  // Register/unregister a per-number message listener.
  const addMessageListener = useCallback((phoneNumber: string, listener: SMSMessageListener) => {
    let set = listenersRef.current.get(phoneNumber);
    if (!set) {
      set = new Set();
      listenersRef.current.set(phoneNumber, set);
    }
    set.add(listener);
    return () => {
      listenersRef.current.get(phoneNumber)?.delete(listener);
    };
  }, []);

  // Single shared realtime subscription: postgres_changes INSERT on sms_messages.
  // RLS scopes delivery to the user's assigned numbers (per-number policy), so ONE channel
  // safely feeds every number — no per-component channels, hence no same-topic collisions.
  // Each row is routed to its number's listeners; inbound rows also bump the unread count.
  useEffect(() => {
    if (!user) return;

    const channel = supabase.channel("sms_messages:feed");
    channel
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "sms_messages" }, (payload) => {
        const row = payload.new as any;
        const number = row.twilio_number as string;
        const message = mapRowToSMSMessage(row);

        if (row.direction === "inbound") {
          lastFetchRef.current[number] = 0; // bypass debounce so the recount runs now
          void refreshUnreadCount(number);
        }

        listenersRef.current.get(number)?.forEach((cb) => cb(message));
      })
      .subscribe((status) => {
        console.log("[SMSProvider] sms_messages feed status:", status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, refreshUnreadCount]);

  // Initial unread fetch when phones load / change.
  useEffect(() => {
    if (phones.length === 0) return;
    phones.forEach((phone) => refreshUnreadCount(phone.number));
  }, [phones, refreshUnreadCount]);

  // Refresh on app foreground.
  useEffect(() => {
    if (phones.length === 0) return;

    const handleAppStateChange = (state: AppStateStatus) => {
      if (state === "active") {
        phones.forEach((phone) => refreshUnreadCount(phone.number));
      }
    };

    const subscription = AppState.addEventListener("change", handleAppStateChange);
    return () => subscription?.remove();
  }, [phones, refreshUnreadCount]);

  return (
    <SMSContext.Provider
      value={{
        unreadCounts,
        phones,
        totalUnread,
        refreshUnreadCount,
        refreshAllUnreadCounts,
        markConversationRead,
        addMessageListener,
      }}
    >
      {children}
    </SMSContext.Provider>
  );
}
